import React, { useState } from "react";
import {
  List,
  Datagrid,
  TextField,
  Show,
  Edit,
  Create,
  SimpleForm,
  TextInput,
  DateField,
  required,
  SearchInput,
  AutocompleteArrayInput,
  TabbedShowLayout,
  Tab,
  SimpleShowLayout,
  NumberField,
  FilterButton,
  CreateButton,
  TopToolbar,
  BooleanField,
  BooleanInput,
  NumberInput,
  useRedirect,
  useNotify,
} from "react-admin";
import StringArrayField from "../components/StringArrayField";
import { USER_ROLES } from "../consts";
import ExportAllButton from "../components/ExportAllButton";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField as MuiTextField,
  IconButton,
  Box,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";

const filters = [<SearchInput source="q_name" alwaysOn />];

const ListActions = () => (
  <TopToolbar>
    <FilterButton />
    <CreateButton />
    <ExportAllButton />
  </TopToolbar>
);

export const ApiTokenList = () => (
  <List filters={filters} perPage={25} actions={<ListActions />}>
    <Datagrid rowClick="show">
      <TextField source="name" />
      <TextField source="roles" />
      <BooleanField source="enabled" />
      <DateField source="lastUsedAt" />
      <DateField source="expiresAt" />
      <DateField source="createdAt" />
      <DateField source="updatedAt" />
    </Datagrid>
  </List>
);

export const ApiTokenRowShow = () => (
  <SimpleShowLayout>
    <TextField source="description" />
  </SimpleShowLayout>
);

export const ApiTokenShow = () => (
  <Show>
    <TabbedShowLayout>
      <Tab label="Token Details">
        <TextField source="name" />
        <TextField source="description" />
        <StringArrayField
          source="roles"
          transform={(value: string) =>
            USER_ROLES?.find((role) => role.id === value)?.name || value
          }
        />
        <BooleanField source="enabled" />
        <TextField source="tokenPreview" />
        <NumberField
          label="TTL (days from creation)"
          source="expiresAfterDays"
        />
        <DateField source="expiresAt" />
        <DateField source="lastUsedAt" />
        <DateField source="createdAt" />
        <DateField source="updatedAt" />
      </Tab>
    </TabbedShowLayout>
  </Show>
);

export const ApiTokenEdit = () => {
  return (
    <Edit redirect="show" mutationMode="pessimistic">
      <SimpleForm>
        <TextInput source="name" />
        <TextInput source="description" />
        <AutocompleteArrayInput source="roles" choices={USER_ROLES} />
        <BooleanInput source="enabled" />
        <NumberInput source="expiresAfterDays" />
      </SimpleForm>
    </Edit>
  );
};

const TokenModal = ({
  open,
  onClose,
  token,
  notify,
}: {
  open: boolean;
  onClose: () => void;
  token: string;
  notify: any;
}) => {
  const handleCopy = () => {
    navigator.clipboard.writeText(token);
    notify("Copied to clipboard", { type: "success" });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">API Token Created</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Copy this token and save it somewhere safe. You won't be able to see
          it again.
        </Typography>
        <Box display="flex" alignItems="center" gap={1} mt={2}>
          <MuiTextField
            fullWidth
            value={token}
            InputProps={{
              readOnly: true,
            }}
            variant="outlined"
            size="small"
          />
          <IconButton onClick={handleCopy} color="primary" size="small">
            <ContentCopyIcon />
          </IconButton>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const ApiTokenCreate = () => {
  const redirect = useRedirect();
  const notify = useNotify();
  const [modalOpen, setModalOpen] = useState(false);
  const [token, setToken] = useState("");
  const [tokenId, setTokenId] = useState("");

  const onSuccess = (data: any) => {
    setToken(data.tokenPreview);
    setTokenId(data.id);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    redirect("show", "admin/api-tokens", tokenId);
  };

  return (
    <>
      <Create
        redirect={false}
        mutationMode="pessimistic"
        mutationOptions={{ onSuccess }}
      >
        <SimpleForm>
          <TextInput source="name" validate={[required()]} />
          <TextInput source="description" />
          <BooleanInput source="enabled" defaultValue={true} />
          <NumberInput source="expiresAfterDays" defaultValue={365} />
          <AutocompleteArrayInput source="roles" choices={USER_ROLES} />
        </SimpleForm>
      </Create>
      <TokenModal
        open={modalOpen}
        onClose={handleCloseModal}
        token={token}
        notify={notify}
      />
    </>
  );
};
